import * as _ from 'lodash';

import { DEFAULT_FONT_SIZE } from "../constants";

interface WrapResult {
    lines: ReadonlyArray<string>,
    width: number,
}

interface NoWrapResult {
    lines: ReadonlyArray<string>,
    width: number,
    height: number,
}

interface FitTextBoxResult {
    lines: ReadonlyArray<string>,
    size: number,
    width: number,
    height: number,
}

const canvas = document.getElementById('ruler') as HTMLCanvasElement;
const ctx = canvas.getContext('2d');

const font = '"Gloria Hallelujah", cursive';

function calcTextWidth(text: string, size: number = 0): number {
    if (ctx) {
        if (size > 0) {
            ctx.font = `${size}px ${font}`;
        }
        const metrics = ctx.measureText(text.trim());
        return metrics.width;
    }
    throw new Error("Unable to measure text");
}

function wordWrap(text: string, maxWidth: number, size: number = DEFAULT_FONT_SIZE): WrapResult {
    if (!ctx) {
        throw new Error("Text measurement was not initialized");
    }
    ctx.font = `${size}px ${font}`;

    text = text.trim();
    if (! text.includes("\n")) {
        const all = calcTextWidth(text);
        if (all < maxWidth) {
            return {lines: [text], width: all};
        }
    }

    let lines: string[] = [];
    text = text + ' ';
    let fromIndex = 0;
    let width = 0;
    for (let lastToIndex = 0, toIndex = text.indexOf(' '); toIndex > -1; toIndex = text.indexOf(' ', lastToIndex)) {
        const currentWidth = calcTextWidth(text.substring(fromIndex, toIndex));
        if (currentWidth > maxWidth) {
            if (lastToIndex === fromIndex) {
                lines = [...lines, text.substring(fromIndex, toIndex)];
                lastToIndex = toIndex + 1;
                fromIndex = lastToIndex;
                width = Math.max(width, currentWidth);
            } else {
                lines = [...lines, text.substring(fromIndex, lastToIndex - 1)];
                fromIndex = lastToIndex;
            }
        } else {
            lastToIndex = toIndex + 1;
            width = Math.max(width, currentWidth);
        }
    }
    if (fromIndex < text.length) {
        lines = [...lines, text.substring(fromIndex)];
    }
    return { lines, width };
}

function doParagraphWrap(lines: ReadonlyArray<string>, maxWidth: number, size: number = DEFAULT_FONT_SIZE): WrapResult {
    return lines
        .map(s => wordWrap(s, maxWidth, size))
        .reduce((previousValue, currentValue) =>
            ({
                lines: [...previousValue.lines, ...currentValue.lines],
                width: Math.max(previousValue.width, currentValue.width)
            })
        );
}

export function textNoWrap(paragraph: string, size: number = DEFAULT_FONT_SIZE): NoWrapResult {
    const lines = _.split(paragraph, "\n");
    const width = lines.map(line => calcTextWidth(line, size)).reduce((max, current) => Math.max(max, current));
    const height = (lines.length + (lines.length - 1) * 0.5) * size;
    return { lines, width, height };
}

export function textWrap(paragraph: string, maxWidth: number, size: number = DEFAULT_FONT_SIZE): WrapResult {
    const text = _.split(paragraph, "\n")
        .map(s => s.trim());
    return doParagraphWrap(text, maxWidth, size);
}

export function fitTextBox(paragraph: string, maxWidth: number, maxHeight: number): FitTextBoxResult {
    const text = _.split(paragraph, "\n")
        .map(s => s.trim());

    let result = {
        lines: [paragraph] as ReadonlyArray<string>,
        size: maxHeight,
        width: maxWidth,
        height: maxHeight,
    };

    let minSize = 0;
    let maxSize = maxHeight;
    while (minSize <= maxSize) {
        const size = Math.floor((minSize + maxSize) / 2);
        const wordWrapResult = doParagraphWrap(text, maxWidth, size);
        // if (! wordWrapResult) {
        //     maxSize = size - 1;
        //     continue;
        // }
        const { lines, width } = wordWrapResult;
        const height = (lines.length + (lines.length - 1) * 0.5) * size;
        if (width < maxWidth && height < maxHeight) {
            minSize = size + 1;
            result = { lines, size, width, height };
        } else {
            maxSize = size - 1;
        }
    }

    return result;
}


// WEBPACK FOOTER //
// ./src/services/text-service.ts